import { useQuery } from "@tanstack/react-query";
import { listEmailTemplates, type EmailTemplate } from "@/api/client";
import { useSession } from "@/auth/session";

export const MERGE_FIELDS = [
  "{{full_name}}",
  "{{first_name}}",
  "{{program}}",
  "{{certificate_no}}",
  "{{membership_no}}",
  "{{issued_date}}",
  "{{induction_date}}",
] as const;

/**
 * Live email templates to choose from, or null when the user can't send (the API refuses the read) or they are still loading.
 * archivedName names a saved id that is no longer live.
 */
export function useEmailTemplateChoices(): {
  templates: EmailTemplate[] | null;
  archivedName: (id: string) => string;
  error: unknown;
  isLoading: boolean;
} {
  const { can } = useSession();
  const allowed = can("certificate.send");
  const templates = useQuery({
    queryKey: ["email-templates", { archived: true }],
    queryFn: () => listEmailTemplates({ includeArchived: true }),
    enabled: allowed,
  });
  return {
    // Null while loading, so a saved id isn't taken for archived before the list arrives.
    templates: allowed && templates.data ? templates.data.filter((t) => !t.archived_at) : null,
    archivedName: (id) => {
      const found = templates.data?.find((t) => t.id === id);
      return found ? `${found.name} (archived)` : "Archived template";
    },
    error: templates.error,
    isLoading: allowed && templates.isLoading,
  };
}
